import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleListItem from "@/components/feature/ArticleListItem";
import Pagination from "@/components/feature/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import { articleList } from "@/mocks/articles";
import { starTagOptions } from "@/mocks/categories";

const PER_PAGE = 20;

export default function StarNewsPage() {
  usePageMeta(
    "明星动态 - 蜜桃传媒",
    "蜜桃传媒明星动态专栏聚焦明星采访、活动现场与片场花絮，按发布时间实时更新，带你了解台前幕后的新鲜故事。"
  );

  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [activeTags]);

  const starArticles = useMemo(
    () =>
      articleList
        .filter((item) => item.section === "star")
        .sort((a, b) => (a.date < b.date ? 1 : -1)),
    []
  );

  const filtered = useMemo(
    () =>
      activeTags.length === 0
        ? starArticles
        : starArticles.filter((item) => item.tags.some((tag) => activeTags.includes(tag))),
    [starArticles, activeTags]
  );

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const toggleTag = (tag: string) => {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]
    );
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "明星动态" }]} />

      <header className="mt-4 rounded-lg bg-accent-50 p-6 md:p-8">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500 text-background-50">
            <i className="ri-user-star-line text-2xl"></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              明星动态
            </h1>
            <p className="text-sm font-medium text-accent-800">Star News · 台前幕后</p>
          </div>
        </div>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
          本栏目聚焦明星新闻与日常动态，涵盖采访对话、活动现场、片场记录与幕后花絮。内容按发布时间倒序排列，帮助你第一时间了解创作者们的最新状态。
        </p>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <span className="text-sm text-foreground-500">标签筛选：</span>
        <button
          type="button"
          onClick={() => setActiveTags([])}
          className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            activeTags.length === 0
              ? "bg-primary-500 text-background-50"
              : "bg-background-200 text-foreground-700 hover:bg-background-300"
          }`}
        >
          全部
        </button>
        {starTagOptions.map((tag) => {
          const active = activeTags.includes(tag);
          return (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-200 text-foreground-700 hover:bg-background-300"
              }`}
            >
              {tag}
            </button>
          );
        })}
      </div>

      <p className="mt-4 text-sm text-foreground-600">
        共 <strong className="text-foreground-900">{filtered.length}</strong> 条动态
        {totalPages > 1 && (
          <span className="ml-1 text-foreground-500">
            · 第 {page} / {totalPages} 页
          </span>
        )}
      </p>

      {paged.length > 0 ? (
        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {paged.map((item) => (
            <ArticleListItem key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 p-10 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-user-star-line text-3xl"></i>
          </span>
          <h2 className="mt-4 font-heading text-lg font-black text-foreground-950">
            该标签下暂无新闻
          </h2>
          <p className="mt-2 text-sm text-foreground-600">换个标签看看，或浏览全部明星动态。</p>
          <button
            type="button"
            onClick={() => setActiveTags([])}
            className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-refresh-line"></i>
            查看全部
          </button>
        </div>
      )}

      <Pagination current={page} total={totalPages} onChange={setPage} />

      <div className="mt-10 rounded-lg bg-secondary-100 p-6 text-center">
        <h3 className="font-heading text-base font-black text-foreground-950">
          还想看更多影视资讯？
        </h3>
        <p className="mt-2 text-sm text-foreground-600">前往分类页面，按类型与热度浏览海量内容。</p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link
            to="/category/movie"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
          >
            浏览电影
          </Link>
          <Link
            to="/category/tv"
            className="whitespace-nowrap rounded-md border border-secondary-300 bg-background-50 px-4 py-2.5 text-sm font-bold text-foreground-800 hover:border-primary-300"
          >
            浏览电视剧
          </Link>
        </div>
      </div>
    </div>
  );
}